import { Camera, CameraPreset } from './Camera';
import { FlyCamera } from './FlyCamera';
import { FirstPersonCamera } from './FirstPersonCamera';
import { Vector3, CameraType, UUID } from '../types';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';

export class CameraManager {
    // Camera storage
    private _cameras: Map<UUID, Camera> = new Map();
    private _activeCamera: Camera | null = null;
    private _previousCamera: Camera | null = null;
    
    // Transition state
    private _isTransitioning: boolean = false;
    private _transitionProgress: InterpolatedProperty<number> = new InterpolatedProperty('Transition Progress', 0, 0, 1.0);
    private _transitionDuration: number = 2.0;
    
    // Default cameras
    private _defaultFlyCamera: FlyCamera | null = null;
    private _defaultFirstPersonCamera: FirstPersonCamera | null = null;

    constructor() {
        // Simplified constructor without default cameras for now
    }

    // Camera management
    addCamera(camera: Camera): UUID {
        this._cameras.set(camera.id, camera);
        
        // Set as active if no active camera
        if (!this._activeCamera) {
            this.setActiveCamera(camera.id, 0);
        }
        
        return camera.id;
    }

    removeCamera(cameraId: UUID): boolean {
        const camera = this._cameras.get(cameraId);
        if (!camera) return false;
        
        // Deactivate if it's the active camera
        if (this._activeCamera?.id === cameraId) {
            camera.setActive(false);
            this._activeCamera = null;
            
            // Switch to first available camera
            const firstCamera = this._cameras.values().next().value;
            if (firstCamera && firstCamera.id !== cameraId) {
                this.setActiveCamera(firstCamera.id, 0);
            }
        }
        
        this._cameras.delete(cameraId);
        return true;
    }

    getCamera(cameraId: UUID): Camera | null {
        return this._cameras.get(cameraId) || null;
    }

    getCameraByName(name: string): Camera | null {
        for (const camera of this._cameras.values()) {
            if (camera.name === name) {
                return camera;
            }
        }
        return null;
    }

    getAllCameras(): Camera[] {
        return Array.from(this._cameras.values());
    }

    getCamerasByType(type: CameraType): Camera[] {
        return Array.from(this._cameras.values()).filter(camera => camera.cameraType === type);
    }

    // Active camera management
    setActiveCamera(cameraId: UUID, transitionDuration: number = 2.0): boolean {
        const newCamera = this._cameras.get(cameraId);
        if (!newCamera || newCamera === this._activeCamera) return false;
        
        // Store previous camera for transition
        this._previousCamera = this._activeCamera;
        
        // Deactivate previous camera
        if (this._previousCamera) {
            this._previousCamera.setActive(false);
        }
        
        // Set new active camera
        this._activeCamera = newCamera;
        this._activeCamera.setActive(true);
        
        // Start transition if duration > 0
        if (transitionDuration > 0 && this._previousCamera) {
            this.startTransition(transitionDuration);
        }
        
        return true;
    }

    getActiveCamera(): Camera | null {
        return this._activeCamera;
    }

    getActiveCameraId(): UUID | null {
        return this._activeCamera?.id || null;
    }

    // Camera shake
    shakeActiveCamera(intensity: number = 1.0, duration: number = 0.5): void {
        if (this._activeCamera) {
            this._activeCamera.shake(intensity, duration);
        }
    }

    // Transition management
    private startTransition(duration: number): void {
        this._isTransitioning = true;
        this._transitionDuration = duration;
        this._transitionProgress.setDuration(duration);
        this._transitionProgress.value = 1.0; // Animate from 0 to 1
    }

    isTransitioning(): boolean {
        return this._isTransitioning;
    }

    getTransitionProgress(): number {
        return this._transitionProgress.value ?? 0;
    }

    // Update method (called by main engine loop)
    update(deltaTime: number): void {
        // Update all cameras
        for (const camera of this._cameras.values()) {
            camera.update(deltaTime);
        }
        
        // Handle transition completion
        if (this._isTransitioning && !this._transitionProgress.isInterpolating) {
            this._isTransitioning = false;
            this._previousCamera = null;
        }
    }

    // Cleanup
    destroy(): void {
        // Deactivate all cameras
        for (const camera of this._cameras.values()) {
            camera.setActive(false);
            if ('destroy' in camera) {
                (camera as any).destroy();
            }
        }
        
        this._cameras.clear();
        this._activeCamera = null;
        this._previousCamera = null;
        this._isTransitioning = false;
    }
} 